// Stencil
import {
  Component,
  Host,
  h,
  Prop,
  Element,
  State,
  getAssetPath
} from "@stencil/core";
// Other Libraries
import { MercuryBundles, getIconPath } from "@genexus/mercury";

// Custom Imports
import { Locale } from "../../common/locale";

const ACTIVE_SLIDE_SELECTOR = "active";
const ARROW_LEFT_KEY = "ArrowLeft";
const ARROW_RIGHT_KEY = "ArrowRight";
const ESCAPE_KEY = "Escape";
const SLIDE_FADE_DURATION = 300;
const SLIDE_WORD_SELECTOR = ".slide__word";
const SLIDE_WORD_SELECTOR_TIMEOUT = 70;
const SLIDE_WORD_SELECTOR_VISIBLE = "slide__word--visible";

// icons
const ICON_NEXT = getAssetPath(
  `./gx-ide-assets/welcome-page/images/icon-next.svg`
);
const CLOSE_ICON = getIconPath({
  category: "system",
  name: "close",
  colorType: "interactive"
});

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "utils/typography",
  "components/icon",
  "components/button"
];

@Component({
  tag: "gx-ide-welcome-page",
  styleUrl: "welcome-page.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/welcome-page"]
})
export class GxIdeWelcomePage {
  /**
   * The component hard-coded strings translations.
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #componentLocale: any;

  #slidesDescriptionAsChunksAlreadyDisplayed: Map<number, boolean> = new Map();
  #slidesElements: HTMLElement[] = [];
  #slidesElementsRef: HTMLElement[] = [];
  // #slidesDescriptionAsChunksAlreadyDisplayed is used to know if the description of a slide
  // that is set to be displayed as chunks, has already been displayed. Used to not display
  // as chunks after the first time.
  #slideTimeOutId: NodeJS.Timeout;

  @Element() el: HTMLGxIdeWelcomePageElement;

  /**
   * The current active slide index from 'slides' Prop. It does not considers the very first slide "Copyright".
   */
  @State() activeSlideIndex: number = 0;

  /**
   * When true the slider will be visible. Used to display and hide the slide with a smooth transition.
   */
  @State() displaySlider: boolean = false;

  /**
   * True if the very first slide is visible (The copyright slide)
   */
  @State() copyrightsVisible: boolean = true;

  /**
   * True if the user has reached the last slide. Used to hide/disable the navigation.
   */
  @State() lastSlideReached: boolean = false;

  /**
   * When true the slider will be visible. Used to display and hide the slider with a smooth transition.
   */
  @State() sliderVisible: boolean = false;

  /**
   * True if a slide is transitioning, false otherwise. Used to disable the navigation arrows while transitioning.
   */
  @State() slideTransitioning: boolean = false;

  /**
   * True if the user has changed slides by clicking on the navigation dots, or by using the keyboard arrows. This is used to know evaluate the slide transition setTimeOut. If the user has navigated, automatic slide transition should be turned off.
   */
  @State() userHasChangedSlide: boolean = false;

  /**
   * The transition type between the slides. "static" requires the user to navigate by clicking the navigation dots. "auto" will transition automatically after a calculated amount of time.
   */
  @Prop() readonly transitionType: TransitionType = "auto";

  /**
   * The estimated amount of words per minute that the user requires to read a slide. This value is used to return the time each slide is freezed before transitioning to the next one. Only evaluated when transitionType is set to "auto".
   */
  @Prop() readonly wordsPerMinute: number = 250;

  /**
   * Callback invoked when the user closes the slider by pressing the close button.
   */
  @Prop() readonly closeCallback!: CloseCallback;

  /**
   * An array of slides, with the information that each slide requires. This slides appear after the first slide (Copyrights) that is defined inside the component.
   */
  @Prop() readonly slides: SlideInfo[] = [];

  async componentWillLoad() {
    this.#componentLocale = await Locale.getComponentStrings(this.el);
    this.#renderSlides();
  }

  componentDidLoad() {
    this.#slidesElementsRef[this.activeSlideIndex].classList.add(
      ACTIVE_SLIDE_SELECTOR
    );
    this.sliderVisible = true;
    this.#initiateSlidesTransition();
  }

  #closeSlider = () => {
    this.sliderVisible = false;
    setTimeout(() => {
      // wait until the slide disappears, then remove.
      this.el.remove();
      this.closeCallback();
      document.removeEventListener("keydown", this.#keyboardEventHandler);
    }, SLIDE_FADE_DURATION);
  };

  #initiateSlidesTransition = () => {
    this.#slideTimeOut();
    // listen to keydown events to navigate the slides with the arrows
    document.addEventListener("keydown", this.#keyboardEventHandler);
  };

  #displayWords = (wordsNodeList: NodeListOf<HTMLElement>) => {
    wordsNodeList.forEach((word, index) => {
      setTimeout(() => {
        word.classList.add(SLIDE_WORD_SELECTOR_VISIBLE);
      }, index * SLIDE_WORD_SELECTOR_TIMEOUT);
    });
  };

  #dotClickedHandler = (event: MouseEvent) => {
    this.userHasChangedSlide = true;
    const itemClicked = event.currentTarget as HTMLElement;
    const index = parseInt(itemClicked.getAttribute("data-index"), 10);
    this.#updateActiveSlide(index);
  };

  #keyboardEventHandler = (event: KeyboardEvent) => {
    this.userHasChangedSlide = true;
    const goToPrevSlide =
      event.key === ARROW_LEFT_KEY &&
      !this.slideTransitioning &&
      !this.lastSlideReached &&
      this.activeSlideIndex !== 0;

    const goToNextSlide =
      event.key === ARROW_RIGHT_KEY &&
      !this.slideTransitioning &&
      !this.lastSlideReached;

    const closeSlider =
      event.key === ESCAPE_KEY ||
      (event.key === ARROW_RIGHT_KEY &&
        !this.slideTransitioning &&
        this.lastSlideReached);

    if (goToNextSlide) {
      this.#nextSlideHandler();
    } else if (goToPrevSlide) {
      this.#prevSlideHandler();
    } else if (closeSlider) {
      this.#closeSlider();
    }
  };

  #nextSlideHandler = () => {
    const newIndex = this.activeSlideIndex + 1;
    this.#updateActiveSlide(newIndex);
  };

  #prevSlideHandler = () => {
    const newIndex = this.activeSlideIndex - 1;
    this.#updateActiveSlide(newIndex);
  };

  #slideTimeOut = () => {
    // Clear any existing timeout
    if (this.#slideTimeOutId) {
      clearTimeout(this.#slideTimeOutId);
    }

    const autoChangeSlide =
      this.transitionType === "auto" &&
      !this.lastSlideReached &&
      !this.userHasChangedSlide;

    if (!autoChangeSlide) {
      return;
    }

    let itemsWordsCount = 0;
    if (this.slides[this.activeSlideIndex].listItems) {
      const itemsWords = this.slides[this.activeSlideIndex].listItems.map(
        item => {
          return item.title + item.description;
        }
      );
      if (itemsWords.length) {
        const mergedItemsWords = itemsWords.join("");
        itemsWordsCount = mergedItemsWords.split(" ").length;
      }
    }

    const mergedDescription =
      this.slides[this.activeSlideIndex].description.join("");
    const words = mergedDescription.split(" ").length;
    const timeBeforeNextSlide: number = this.#secondsToRead(
      words + itemsWordsCount
    );

    // Set a fresh timeout
    if (timeBeforeNextSlide) {
      this.#slideTimeOutId = setTimeout(() => {
        this.#nextSlideHandler();
      }, timeBeforeNextSlide);
    }
  };

  #secondsToRead = (
    wordsCount: number,
    wordsPerMinute = this.wordsPerMinute
  ): number => {
    const minutes = wordsCount / wordsPerMinute;
    const milliseconds = minutes * 60000; // Convert minutes to milliseconds
    return milliseconds;
  };

  #renderSlides = () => {
    this.#slidesElements = this.slides.map((slide, index) => {
      const isLastSlide = index === this.slides.length - 1;
      return (
        <section
          class="slide__wrapper"
          ref={(el: HTMLElement) =>
            (this.#slidesElementsRef[index] = el as HTMLElement)
          }
        >
          <div
            class="slide__illustration"
            role="img"
            aria-label={this.#componentLocale.copyright.illustrationAriaLabel}
          >
            <img
              class="slide__illustration-img"
              src={slide.illustration.url}
              alt={slide.illustration.alt}
            />
          </div>
          <section class="slide__copy">
            <div class="slide__copy-container">
              <header class="slide__copy-header">
                <img src={ICON_NEXT} alt="GeneXus Next isologotype" />
                <h2 class="slide__copy-title heading-2">{slide.title}</h2>
              </header>
              <div class="slide__copy-description">
                {slide.description?.map(paragraph => {
                  return (
                    <p>
                      {slide.descriptionAsChunks
                        ? this.#splitWords(paragraph)
                        : paragraph}
                    </p>
                  );
                })}
                {slide.listItems?.length && (
                  <ul class="slide__list">
                    {slide.listItems.map(item => {
                      return (
                        <li class="slide__list-item">
                          <strong class="slide__list-item-title">
                            {item.title}
                          </strong>{" "}
                          {item.description}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              {isLastSlide && (
                <button
                  class="button-primary start-button"
                  onClick={this.#closeSlider}
                >
                  {this.#componentLocale.startButtonCaption}
                </button>
              )}
            </div>
          </section>
        </section>
      );
    });
  };

  #splitWords = (paragraph: string): HTMLSpanElement[] => {
    return paragraph.split(" ").map(word => {
      // keep the space after {word} !
      return <span class="slide__word">{word} </span>;
    });
  };

  #updateActiveSlide = (newSlideIndex: number) => {
    this.slideTransitioning = true;

    // unset current active slide
    const activeSlide = this.#slidesElementsRef[this.activeSlideIndex];
    activeSlide.classList.remove(ACTIVE_SLIDE_SELECTOR);

    const nextSlideReference = this.#slidesElementsRef[newSlideIndex];
    const displayDescriptionAsChunks =
      this.slides[newSlideIndex].descriptionAsChunks;
    const descriptionAsChunksDisplayedAlready =
      this.#slidesDescriptionAsChunksAlreadyDisplayed.get(newSlideIndex);
    let wordsCount;
    let wordsNodeList: NodeListOf<HTMLElement>;
    let slideTimeOut = 0;
    if (displayDescriptionAsChunks) {
      wordsNodeList = nextSlideReference.querySelectorAll(SLIDE_WORD_SELECTOR);
      wordsCount = wordsNodeList.length;
      slideTimeOut = wordsCount * SLIDE_WORD_SELECTOR_TIMEOUT;
    }

    this.lastSlideReached = newSlideIndex === this.slides.length - 1;

    // set new slide as active after fade out
    setTimeout(() => {
      nextSlideReference.classList.add(ACTIVE_SLIDE_SELECTOR);

      if (displayDescriptionAsChunks && !descriptionAsChunksDisplayedAlready) {
        this.#slidesDescriptionAsChunksAlreadyDisplayed.set(
          newSlideIndex,
          true
        );
        this.#displayWords(wordsNodeList);
        setTimeout(() => {
          this.slideTransitioning = false;
        }, slideTimeOut);
      } else {
        this.slideTransitioning = false;
      }
    }, SLIDE_FADE_DURATION);

    // update active slide index
    this.activeSlideIndex = newSlideIndex;
    this.#slideTimeOut(); // this has to be after this.#updateActiveSlide.
  };

  render() {
    return (
      <Host>
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <div
          class={{
            "main": true,
            "main--visible": this.sliderVisible,
            "main--border-glow": this.lastSlideReached
          }}
          style={{ "--slide-fade-duration": `${SLIDE_FADE_DURATION}ms` }}
        >
          <section class="slide">
            <header class="slide__header">
              <button
                class="button-tertiary button-icon-only close-icon"
                aria-label="close presentation"
                onClick={this.#closeSlider}
              >
                <ch-image class="icon-m" src={CLOSE_ICON}></ch-image>
              </button>
            </header>
            <div class="slide__container">{this.#slidesElements}</div>
            <footer
              // slide common footer
              class={{
                slide__footer: true
              }}
            >
              <div
                class={{
                  "slide__footer-wrapper": true
                }}
              >
                <ul class="slide__dots" aria-label="Slide indicators">
                  {this.slides.map((_, index) => {
                    return (
                      <li
                        class={{
                          "slide__dot": true,
                          "slide__dot-inactive": this.slideTransitioning,
                          [ACTIVE_SLIDE_SELECTOR]:
                            index === this.activeSlideIndex
                        }}
                        data-index={index}
                        onClick={
                          !this.slideTransitioning && this.#dotClickedHandler
                        }
                      ></li>
                    );
                  })}
                </ul>
              </div>
            </footer>
          </section>
        </div>
      </Host>
    );
  }
}

export type SlideInfo = {
  illustration: {
    url: string;
    alt: string;
  };
  title: string;
  description?: string[];
  descriptionAsChunks?: boolean;
  listItems?: Array<{
    title: string;
    description: string;
  }>;
};

export type CloseCallback = () => Promise<void>;
export type TransitionType = "static" | "auto";
